import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import Text from '@/components/Typography/Text';
import ToolRunButton from './ToolRunButton';
import { resolveBoundField } from './ToolFormField';
import { getImageToolRetryInputs, getImageToolRetryModel } from './imageToolRetry';
import { useAuthStore, useModelStore } from '@/store';
import { toolsApi } from '@/lib/api';
import { useTranslation } from '@/hooks/useTranslation';
import { formatCreditBalance } from '@/lib/formatCredits';

export default function ToolModelRetry({ tool, run, onRetry, disabled = false }) {
  const { t } = useTranslation();
  const user = useAuthStore(state => state.user);
  const models = useModelStore(state => state.models);
  const model = useMemo(() => getImageToolRetryModel(tool, run, { ...useModelStore.getState(), models }, user), [tool, run, models, user]);
  const inputs = useMemo(() => model ? getImageToolRetryInputs(tool, run, model, resolveBoundField) : null, [tool, run, model]);
  const [quote, setQuote] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const retryingRef = useRef(false);

  useEffect(() => {
    if (!model || !onRetry) return;
    let cancelled = false;
    setQuote(null);
    toolsApi.calculateCredits(tool.slug, { inputs, modelId: model.id }).then(result => {
      const cost = result?.cost?.total;
      if (!cancelled) setQuote({ inputs, modelId: model.id, cost: typeof cost === 'number' && Number.isFinite(cost) && cost >= 0 ? cost : null });
    }).catch(() => {
      if (!cancelled) setQuote({ inputs, modelId: model.id, cost: null });
    });
    return () => { cancelled = true; };
  }, [tool?.slug, model, inputs, onRetry]);

  if (!model || !onRetry) return null;
  const currentQuote = quote?.inputs === inputs && quote?.modelId === model.id ? quote : null;
  const ready = currentQuote?.cost != null;
  const retry = async () => {
    if (!ready || disabled || retryingRef.current) return;
    retryingRef.current = true;
    setIsRetrying(true);
    try {
      await onRetry(model, inputs);
    } finally {
      retryingRef.current = false;
      setIsRetrying(false);
    }
  };

  return (
    <View style={{ gap: 10, marginTop: 16 }}>
      <Text size='n' weight='semibold' color='primary'>
        {t('pages.tools.retryDifferentModelTitle') || 'Try again with a different model'}
      </Text>
      <Text size='s' color='secondary'>
        {t('pages.tools.retryDifferentModelCopy') || 'Use the same prompt and images with another model.'}
      </Text>
      <ToolRunButton
        variant='neutral'
        icon='rotate-right'
        label={(t('pages.tools.retryWithModel') || 'Try with %MODEL%').replace('%MODEL%', model.name || model.slug)}
        loadingLabel={t('pages.tools.retryStarting') || 'Starting retry…'}
        isLoading={isRetrying}
        isDisabled={disabled || !ready}
        onPress={retry}
      />
      <Text size='xs' color='secondary'>
        {ready
          ? (t('pages.tools.retryCreditCost') || '%COUNT% credits for this retry').replace('%COUNT%', formatCreditBalance(currentQuote.cost))
          : currentQuote
            ? (t('pages.tools.retryQuoteFailed') || 'Could not load the retry price. Choose another model above to try again.')
            : (t('pages.tools.retryLoadingPrice') || 'Checking retry price…')}
      </Text>
    </View>
  );
}
